'use client';

import { Json } from '@/types/database.types';
import { createClient } from '@/utils/supabase/client';
import { useTranslations } from 'next-intl';
import DownloadButton from './DownloadButton';
import WatchButton from './WatchButton';

export interface GetUserTicketsResponse {
    tickets: TicketAllocation[];
    benefits: TicketAllocation[];
    gifts?: {
        ticketAllocationId: string;
        ticketId: string;
        name: string | null;
        giftCode: string | null;
        targetTicketId: string | null;
        expiresAt: string | null;
        isRedeemed: boolean | null;
    }[];
}

interface Screening {
    id: string;
    name: string;
    start_time: string;
    end_time: string;
    location_id: string | null;
    streaming_url: string | null;
    screening_type: 'ONLINE' | 'OFFLINE';
}

export interface TicketAllocation {
    ticketId: string;
    ticketAllocationId: string;
    name: string;
    usage_limits: Json;
    ticketPath: string | null;
    screening?: Screening | null;
    voucher: {
        url: string;
        code: string;
        expiresAt: string | null;
        firstAccessedAt: string | null;
    } | null;
}

export default function TicketRowItem({
    ticketAllocationId,
    name,
    usage_limits,
    screening,
    ticketPath,
    voucher,
}: TicketAllocation) {
    const t = useTranslations();
    const supabase = createClient();

    let type = '';

    const usageLimits = Array.isArray(usage_limits)
        ? usage_limits
        : typeof usage_limits === 'string'
          ? JSON.parse(usage_limits)
          : [];

    const isOnline = screening ? screening.screening_type === 'ONLINE' : usageLimits.online;
    const isOffline = screening ? screening.screening_type === 'OFFLINE' : usageLimits.in_cinema;

    const usage = `${isOnline ? t('Cart.virtualExclusive') : ''} ${isOnline && isOffline ? '/' : ''} ${isOffline ? t('Cart.inCinema') : ''}`;

    if (usageLimits.single_use === true || usageLimits.online === 1 || usageLimits.in_cinema === 1)
        type = name;
    if (usageLimits.single_use === false || usageLimits.online > 1 || usageLimits.in_cinema > 1)
        type = t('Common.pass');

    const downloadTicket = async (path: string) => {
        const { data, error } = await supabase.storage.from('tickets').download(path);
        if (error) {
            console.error('Error downloading ticket:', error);
            throw new Error('Error downloading ticket');
        }
        const blob = new Blob([data as BlobPart], { type: 'application/pdf' });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', path);
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    return (
        <div className="flex items-center gap-5 py-5 border-b border-white/50 lg:justify-between max-lg:w-fit text-md lg:text-h6 uppercase text-white/50 max-md:pr-10">
            <div className="max-w-[400px] min-w-[400px] w-full text-md lg:text-h6 uppercase text-white">
                {screening ? screening.name : name}
            </div>
            <div className="max-w-[250px] min-w-[250px] w-full">{type}</div>
            <div className="max-w-[200px] min-w-[200px] w-full">{usage}</div>
            <div className="max-w-[100px] min-w-[100px] w-full flex flex-col">
                {ticketPath && (
                    <DownloadButton onClick={() => downloadTicket(ticketPath)} name={ticketPath} />
                )}
                {screening && (screening.streaming_url || voucher) && (
                    <WatchButton
                        ticketAllocationId={ticketAllocationId}
                        cinesendVideoId={screening.streaming_url || ''}
                        voucherCode={voucher}
                    />
                )}
                {/* {noPath && (
                    <button
                        className="uppercase text-primary"
                        onClick={() => router.push(PathEnum.Films)}
                    >
                        {t('Checkout.use')}
                    </button>
                )} */}
            </div>
        </div>
    );
}
